import React from "react"
import {useIntl, useIntlEx} from "./IntlUtils";
import {shortIdEqual} from "./StringUtils";
import {TagBox, TagLabel} from "./TagBox";
import {
  ADMINISTRATOR_USER_LEVEL,
  MANAGER_USER_LEVEL,
  OPERATOR_USER_LEVEL,
  OWNER_USER_LEVEL,
  REPORTER_USER_LEVEL,
  SUPERVISOR_USER_LEVEL,
  useSolv
} from "./SolvProvider";
import getLogger from "./Logging.js"
import {SelectDividerOption} from "./FormComps";
import {optsAddTextAndDescription} from "./Utils";

const log = getLogger("Member")

export function useUserStatusOpts() {
  const {intl} = useIntlEx()
  const userStatusOpts = [
    {
      value: "NEW",
      label: intl.msg("status_account_new"),
    },
    {
      value: "ACTIVE",
      label: intl.msg("status_account_active"),
    },
    {
      value: "IDLE",
      label: intl.msg("status_account_inactive"),
    },
    {
      value: "DORMANT",
      label: intl.msg("status_account_dormant"),
    },
    {
      value: "BANNED",
      label: intl.msg("status_account_banned"),
    },
    {
      value: "ONBOARDING",
      label: intl.msg("status_account_onboarding"),
    },
    {
      value: "INVITING",
      label: intl.msg("status_account_inviting"),
    },
  ]
  function findUserStatusOpt(status) {
    const st = status?.toUpperCase()
    if (!st) {
      return null
    }
    if (st.startsWith("DORMANT")) {
      return userStatusOpts.find(o => o.value === "DORMANT")
    }
    else {
      return userStatusOpts.find(o => o.value === st)
    }
  }
  return ({
    userStatusOpts,
    findUserStatusOpt,
  })
}

export function useAccessLevelOpts() {
  const {intl} = useIntlEx()
  const accessLevelOpts = [
    {
      value: REPORTER_USER_LEVEL,
      text: intl.msg("access_level_reporter"),
      description: intl.msg("access_level_reporter_description"),
    },
    {
      value: OPERATOR_USER_LEVEL,
      text: intl.msg("access_level_operator"),
      description: intl.msg("access_level_operator_description"),
    },
    {
      value: SUPERVISOR_USER_LEVEL,
      text: intl.msg("access_level_supervisor"),
      description: intl.msg("access_level_supervisor_description"),
    },
    {
      value: MANAGER_USER_LEVEL,
      text: intl.msg("access_level_manager"),
      description: intl.msg("access_level_manager_description"),
    },
    {
      value: ADMINISTRATOR_USER_LEVEL,
      text: intl.msg("access_level_administrator"),
      description: intl.msg("access_level_administrator_description"),
    },
    {
      value: OWNER_USER_LEVEL,
      text: intl.msg("access_level_owner"),
      description: intl.msg("access_level_owner_description"),
    }
  ]
  accessLevelOpts.forEach(o => o.label = <>
    <div>
      {o.text}
    </div>
    <small>
      {o.description}
    </small>
  </>)

  function findAccessLevelOpt(accessLevel) {
    return accessLevelOpts.find(o => o.value === accessLevel)
  }

  function findAvailableAccessLevelOpts(tenant, accessLevel) {
    let opts = []
    if (tenant?.accessingAs("*/INSTREAM/*")) {
      switch (accessLevel) {
        case OWNER_USER_LEVEL:
          opts.push(accessLevelOpts.find(i => i.value === REPORTER_USER_LEVEL))
          opts.push(accessLevelOpts.find(i => i.value === OPERATOR_USER_LEVEL))
          opts.push(accessLevelOpts.find(i => i.value === SUPERVISOR_USER_LEVEL))
          opts.push(accessLevelOpts.find(i => i.value === MANAGER_USER_LEVEL))
          opts.push(accessLevelOpts.find(i => i.value === ADMINISTRATOR_USER_LEVEL))
          opts.push(accessLevelOpts.find(i => i.value === OWNER_USER_LEVEL))
          break
        case ADMINISTRATOR_USER_LEVEL:
          opts.push(accessLevelOpts.find(i => i.value === REPORTER_USER_LEVEL))
          opts.push(accessLevelOpts.find(i => i.value === OPERATOR_USER_LEVEL))
          opts.push(accessLevelOpts.find(i => i.value === SUPERVISOR_USER_LEVEL))
          opts.push(accessLevelOpts.find(i => i.value === MANAGER_USER_LEVEL))
          opts.push(accessLevelOpts.find(i => i.value === ADMINISTRATOR_USER_LEVEL))
          break
        case MANAGER_USER_LEVEL:
          opts.push(accessLevelOpts.find(i => i.value === REPORTER_USER_LEVEL))
          opts.push(accessLevelOpts.find(i => i.value === OPERATOR_USER_LEVEL))
          opts.push(accessLevelOpts.find(i => i.value === SUPERVISOR_USER_LEVEL))
          opts.push(accessLevelOpts.find(i => i.value === MANAGER_USER_LEVEL))
          break
      }
    }
    else if (tenant?.accessingAs("ADMINISTRATOR/DOWNSTREAM/*")) {
      opts.push(accessLevelOpts.find(i => i.value === REPORTER_USER_LEVEL))
      opts.push(accessLevelOpts.find(i => i.value === OPERATOR_USER_LEVEL))
      opts.push(accessLevelOpts.find(i => i.value === SUPERVISOR_USER_LEVEL))
      opts.push(accessLevelOpts.find(i => i.value === MANAGER_USER_LEVEL))
      opts.push(accessLevelOpts.find(i => i.value === ADMINISTRATOR_USER_LEVEL))
    }
    else if (tenant?.accessingAs("MANAGER/DOWNSTREAM/*")) {
      opts.push(accessLevelOpts.find(i => i.value === REPORTER_USER_LEVEL))
      opts.push(accessLevelOpts.find(i => i.value === OPERATOR_USER_LEVEL))
      opts.push(accessLevelOpts.find(i => i.value === SUPERVISOR_USER_LEVEL))
      opts.push(accessLevelOpts.find(i => i.value === MANAGER_USER_LEVEL))
      // opts.push(accessLevelOpts.find(i => i.value === ADMINISTRATOR_USER_LEVEL))
    }
    else if (tenant?.accessingAs("SUPERVISOR/DOWNSTREAM/*")) {
      opts.push(accessLevelOpts.find(i => i.value === REPORTER_USER_LEVEL))
      opts.push(accessLevelOpts.find(i => i.value === OPERATOR_USER_LEVEL))
    }
    return opts
  }

  function findEqualOrHigherAccessLevelOpts(accessLevel) {
    return [...accessLevelOpts].sort(a => a.value).filter(a => a.value >= accessLevel)
  }

  function findInvitableAccessLevelOpts(tenant, accessLevel) {
    let opts = []
    if (tenant?.accessingAs("*/INSTREAM,DOWNSTREAM/*")) {
      switch (tenant.tenantTypeId) {
        case "CLIENT":
        case "SYSTEM":
          switch (accessLevel) {
            case OWNER_USER_LEVEL:
            case ADMINISTRATOR_USER_LEVEL:
              opts = accessLevelOpts.filter(i => [REPORTER_USER_LEVEL, OPERATOR_USER_LEVEL, SUPERVISOR_USER_LEVEL, MANAGER_USER_LEVEL, ADMINISTRATOR_USER_LEVEL].includes(i.value))
              break
            case MANAGER_USER_LEVEL:
              opts = accessLevelOpts.filter(i => [REPORTER_USER_LEVEL, OPERATOR_USER_LEVEL, SUPERVISOR_USER_LEVEL].includes(i.value))
              break
          }
          break
        case "RESELLER":
        case "AFFILIATE":
          switch (accessLevel) {
            case OWNER_USER_LEVEL:
            case ADMINISTRATOR_USER_LEVEL:
              opts = accessLevelOpts.filter(i => [REPORTER_USER_LEVEL, ADMINISTRATOR_USER_LEVEL].includes(i.value))
              break
            case MANAGER_USER_LEVEL:
              opts = accessLevelOpts.filter(i => [REPORTER_USER_LEVEL].includes(i.value))
              break
          }
          break
      }
    }
    return opts
  }

  return ({
    accessLevelOpts,
    findAccessLevelOpt,
    findAvailableAccessLevelOpts,
    findInvitableAccessLevelOpts,
    findEqualOrHigherAccessLevelOpts,
  })
}

export function useNotificationLevelOpts() {
  const {intl} = useIntlEx()
  const notificationLevelOpts = [
    {
      value: 0,
      label: <><div>{intl.msg("member_notification_level_0")}</div><small>{intl.msg("member_notification_level_0_tooltip")}</small></>,
    },
    {
      value: 10,
      label: <><div>{intl.msg("member_notification_level_10")}</div><small>{intl.msg("member_notification_level_10_tooltip")}</small></>,
    },
    {
      value: 20,
      label: <><div>{intl.msg("member_notification_level_20")}</div><small>{intl.msg("member_notification_level_20_tooltip")}</small></>,
    },
    {
      value: 30,
      label: <><div>{intl.msg("member_notification_level_30")}</div><small>{intl.msg("member_notification_level_30_tooltip")}</small></>,
    },
    {
      value: 99,
      label: <><div>{intl.msg("member_notification_level_99")}</div><small>{intl.msg("member_notification_level_99_tooltip")}</small></>,
    },
  ]
  function findNotificationLevelOpt(notificationLevel) {
    return notificationLevelOpts.find(o => o.value === Math.min(Math.max(notificationLevel, 0), 99))
  }
  function findAvailableNotificationLevelOpts(tenant, accessLevel) {
    let opts = []
    // // if (tenant?.accessingAs("SUPERVISOR/MEMBER/*") || tenant?.accessingAs("ADMIN/MANAGER/*")) {
    //   switch (accessLevel) {
    //     case OWNER_USER_LEVEL:
    //     case SUPERADMIN_USER_LEVEL:
    //     case ADMIN_USER_LEVEL:
    //     case SUPERVISOR_USER_LEVEL:
    //       opts.push(notificationLevelOpts.find(i => i.value === 0))
    //       opts.push(notificationLevelOpts.find(i => i.value === 2))
    //       opts.push(notificationLevelOpts.find(i => i.value === 4))
    //       break
    //     default:
    //       if (accessLevel > RESTRICTED_USER_LEVEL) {
    //         opts.push(notificationLevelOpts.find(i => i.value === 0))
    //         opts.push(notificationLevelOpts.find(i => i.value === 2))
    //       }
    //   }
    // // }
    return notificationLevelOpts
  }
  return ({
    notificationOpts: notificationLevelOpts,
    findNotificationLevelOpt: findNotificationLevelOpt,
    findAvailableNotificationLevelOpts: findAvailableNotificationLevelOpts
  })
}

export function useThemeOpts() {

  const {intl} = useIntlEx()

  const themeOpts = optsAddTextAndDescription([
    {
      value: "system",
      text: intl.msg("theme_system"),
      description: intl.msg("theme_system_description"),
    },
    SelectDividerOption(),
    {
      value: "dark",
      text: intl.msg("theme_dark"),
    },
    {
      value: "light",
      text: intl.msg("theme_light"),
    },
    SelectDividerOption(),
    {
      value: "bluefrost-light",
      text: intl.msg("theme_bluefrost")
    },
    {
      value: "orangemocha-dark",
      text: intl.msg("theme_orangemocha"),
    },
    {
      value: "violetnoir-dark",
      text: intl.msg("theme_violetnoir"),
    },
  ])

  function findThemeOpt(value) {
    return themeOpts.find(t=>t.value.toUpperCase() === value?.toUpperCase())
  }

  return ({
    themeOpts,
    findThemeOpt,
  })

}

export function useNotificationOpts() {
  const {brandId, api} = useSolv()
  const {intl} = useIntlEx()
  const notificationOpts = [
    {
      value: "ALL",
      label: "All"
    },
    {
      label: "Categories",
      options: [
        {
          value: "CRITICAL",
          label: "Critical"
        },
        {
          value: "IMPORTANT",
          label: "Important"
        },
        {
          value: "NOTICE",
          label: "Notice"
        },
        {
          value: "INFO",
          label: "Info"
        },
      ]
    },
    {
      label: "Critical Events",
      options: [
        {
          value: "AccountArchived",
          label: "Account Archived",
          category: "CRITICAL",
        },
        {
          value: "BroadcastArchived",
          label: "Broadcast Archived",
          category: "CRITICAL",
        },
        {
          value: "BroadcastRejected",
          label: "Broadcast Rejected",
          category: "CRITICAL",
        },
        {
          value: "ResponsePosted",
          label: "Response Posted",
          category: "CRITICAL",
        },
      ]
    },
    {
      label: "Important Events",
      options: [
        {
          value: "AccountModified",
          label: "Account Modified",
          category: "IMPORTANT",
        },
        {
          value: "BroadcastArchived",
          label: "Broadcast Archived",
          category: "IMPORTANT",
        },
        {
          value: "BroadcastPublished",
          label: "Broadcast Published",
          category: "IMPORTANT",
        },
        {
          value: "BroadcastSubmitted",
          label: "Broadcast Submitted",
          category: "IMPORTANT",
        },
      ]
    },
  ]
  function findNotificationOpt(eventName) {
    if (eventName === "ALL") {
      const o = notificationOpts.find(i => i.value === "ALL")
      // log.debug("findNotificationOpt: ALL: o=", o)
      return o
    }
    else {
      for (const opt of notificationOpts) {
        // log.debug("findNotificationOpt:", opt)
        const r = opt.options?.find(i => i.value === eventName)
        if (r) {
          return r
        }
      }
      return null
    }
  }

  function findAvailableNotificationOpts(tenant, accessLevel) {
    return notificationOpts
  }
  return ({
    status: "UNKNOWN",
    msg: ``
  })
}

export function UserStatus(props) {
  const {session} = useSolv()
  const {intl} = useIntlEx()
  const {findUserStatusOpt} = useUserStatusOpts()
  const userId = props.userId || props.row?.userId
  const statusOpt = findUserStatusOpt(props.status || props.row?.status)
  const userLevel = props.userLevel || props.row?.userLevel
  const size = props.size || "sm"
  return (
    <>
    {
      statusOpt &&
        <TagBox>
          {
            props.showYou && shortIdEqual(session?.user?.userId, userId) &&
              <TagLabel label={intl.msg("you")} className={`user-type-color-you tag-label-${size}`} tooltip={""}/>
          }
          <TagLabel label={statusOpt.value} className={`user-status-color-${statusOpt.value.toLowerCase()} tag-label-${size}`} tooltip={""}/>
        </TagBox>
    }
    </>
  )
}

export function AccessLevel(props) {
  const {findAccessLevelOpt} = useAccessLevelOpts()
  const accessLevelOpt = findAccessLevelOpt(props.userLevel || props.row?.userLevel)
  return (
    accessLevelOpt &&
      <span>
        {accessLevelOpt.text || accessLevelOpt.label}
      </span>
  )
}

export function UserLink(props) {
  const {env} = useSolv()
  const {intl} = useIntlEx()
  if (props && props.user && props.tenant) {
    const session = props.session
    const tenant = props.tenant
    const user = props.user
    const userId = user.userId.replace(/\-/g, "")
    const tenantId = user.tenantId.replace(/\-/g, "")
    const url = `/${tenantId}/members/${userId}`
    return (
      <div className="d-inline-block">
        <div className="d-flex flex-row align-items-center" style={{gap: "4px"}}>
          {
            ["CLIENT"].includes(session?.user?.userTypeId) && ["AFFILIATE", "RESELLER", "SYSTEM"].includes(user.userTypeId) ? (
              <a className="text-link" href={`mailto:${env.SUPPORT_EMAIL}`}>
                System
              </a>
            ) : (
              <a className="text-link" href={url} title={user.emailAddress}>
                {user.fullName || user.emailAddress}
              </a>
            )
          }
        </div>
      </div>
    )
  }
  else {
    return (<></>)
  }
}

export function UserAndTimeLink(props) {
  /*
        //
        //
        //   <>{`${intl.dateTime(timestamp)} `}</>
        // }
        // <span className="text-muted">
        //   &nbsp;
        //   {
        //     props.by ? (
        //       props.by
        //     ) : (
        //       intl.msg("by")
        //     )
        //   }
        //   :&nbsp;
        // </span>
        // <UserLink session={session} tenant={tenant} user={user}/>

   */
  const {intl} = useIntlEx()
  if (props && props.user && props.tenant) {
    const session = props.session
    const tenant = props.tenant
    const user = props.user
    let ts = props.timestamp || props.time || props.dateTime
    if (typeof(ts) === "string") {
      ts = Date.parse(ts)
    }
    return (
      <div className="d-flex flex-row flex-wrap">
        {
          ts ? (
              intl.msg("by_user_and_time", {user: <UserLink session={session} tenant={tenant} user={user}/>, datetime: intl.dateTime(ts)})
            ) : (
              intl.msg("by_user", {user: <UserLink session={session} tenant={tenant} user={user}/>})
            )
        }
      </div>
    )
  }
  else {
    return (<></>)
  }
}



